import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BusinessPartnersService } from '../../../common/services/business-partners.service';
import { BusinessPartner } from '../../../model/business_partner';

@Component({
  selector: 'app-business-partner-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './business-partner-selector.html',
  styleUrl: './business-partner-selector.css'
})
export class BusinessPartnerSelector implements OnInit, OnDestroy {
  @Input() placeholder: string = 'bp_selector.select_business_partner';
  @Input() showLevelFilter: boolean = true;
  @Input() defaultBusinessPartnerName?: string | null;
  @Output() businessPartnerChange = new EventEmitter<BusinessPartner | null>();

  private static readonly STORAGE_KEY = 'bp:lastSelectedName';

  protected open: boolean = false;
  protected searchTerm: string = '';
  protected selectedLevel: number | 'ALL' = 'ALL';
  protected selected: BusinessPartner | null = null;
  protected openLevel: boolean = false;

  protected partners: BusinessPartner[] = [];
  protected levels: number[] = [];
  private initializedSelection: boolean = false;
  private readonly subscriptions = new Subscription();

  constructor(private partnersService: BusinessPartnersService) {}

  ngOnInit(): void {
    this.subscriptions.add(this.partnersService.partners$.subscribe((list) => {
      const enabled = (list || []).filter(p => p.Enabled);
      this.partners = enabled;
      const unique = Array.from(new Set(enabled.map(p => p.Level)));
      this.levels = unique.sort((a, b) => a - b);
      if (this.selectedLevel !== 'ALL' && !this.levels.includes(this.selectedLevel)) {
        this.selectedLevel = 'ALL';
      }

      // Initialize selection from localStorage or provided default, once
      if (!this.initializedSelection && this.partners.length > 0 && !this.selected) {
        try {
          const stored = localStorage.getItem(BusinessPartnerSelector.STORAGE_KEY) || undefined;
          const fallback = (this.defaultBusinessPartnerName || undefined);
          const want = stored || fallback;
          if (want) {
            const match = this.partners.find(p => (p.CompanyName || p.Name) === want);
            if (match) {
              this.selected = match;
              this.businessPartnerChange.emit(match);
            }
          }
        } catch {}
        this.initializedSelection = true;
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected toggleOpen(): void {
    this.open = !this.open;
  }

  protected toggleLevelOpen(event?: Event): void {
    event?.stopPropagation();
    this.openLevel = !this.openLevel;
  }

  protected onSelectLevel(val: number | 'ALL', event?: Event): void {
    event?.stopPropagation();
    this.selectedLevel = val;
    this.openLevel = false;
  }

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.openLevel = false;
    this.open = false;
  }

  protected onSearchChange(val: string): void {
    this.searchTerm = val;
  }

  protected clearSearch(): void {
    this.searchTerm = '';
  }

  protected onLevelChange(val: string): void {
    if (val === 'ALL') {
      this.selectedLevel = 'ALL';
    } else {
      this.selectedLevel = Number(val);
    }
  }

  protected selectPartner(p: BusinessPartner): void {
    this.selected = p;
    try {
      const name = p.CompanyName || p.Name || '';
      if (name) {
        localStorage.setItem(BusinessPartnerSelector.STORAGE_KEY, name);
      }
    } catch {}
    this.open = false;
    this.businessPartnerChange.emit(p);
  }

  protected clearSelection(): void {
    this.selected = null;
    this.businessPartnerChange.emit(null);
  }

  protected get filteredPartners(): BusinessPartner[] {
    const q = (this.searchTerm || '').trim().toLowerCase();
    let list = this.partners.slice();
    if (this.selectedLevel !== 'ALL') {
      list = list.filter(p => p.Level === this.selectedLevel);
    }
    if (!q) { return list; }
    return list.filter(p => {
      const company = String(p.CompanyName || '').toLowerCase();
      return company.includes(q);
    });
  }
}


